import { IconTrashX, IconX } from "@tabler/icons";
import { useTheme } from "styled-components";

import { useApiAccountKeysQuery } from "@sso/apis/apiAccountKeys";
import { Dialog } from "@sso/constant";
import { useAppDispatch, useAppSelector } from "@sso/hooks/useRedux";
import { closeApiKeysTable, selectApiKeysAccountId } from "@sso/store/slices/apiAccount";
import { openDialog } from "@sso/store/slices/dialog";
import { ApiAccountKey } from "@sso/types";
import { Button } from "@sso/ui/Button";
import { CircleLoading } from "@sso/ui/CircleLoading";
import { Divider } from "@sso/ui/Divider";
import { RowContainer, Wrapper } from "@sso/ui/FlexBox";
import { IconButton } from "@sso/ui/IconButton";
import { LayoutTable, TableWrapper, TD, TH } from "@sso/ui/Table";

import { projectsApiKeysTableColumns } from "./APIAccountsTableColumns";

export const APIAccountKeysTable = () => {
  const { colors } = useTheme();
  const dispatch = useAppDispatch();
  const apiAccountId = useAppSelector(selectApiKeysAccountId);

  const handleCloseApiKeysTable = () => dispatch(closeApiKeysTable());

  const handleOpenApiKeysDialog = () => dispatch(openDialog({ type: Dialog.GENERATE_API_KEYS, id: apiAccountId }));

  const handleDeleteApiKeyClick = (id: string) => dispatch(openDialog({ type: Dialog.DELETE_API_ACCOUNT_KEY, id }));

  const { data: accountKeys, isLoading, isError } = useApiAccountKeysQuery(apiAccountId as string);

  const cleanedData: ApiAccountKey[] | undefined = accountKeys?.map(key => ({
    ...key,
    descriptor: key.descriptor || "N/A",
    createDate: new Date(key.createDate).toLocaleDateString(),
    expiryDate: new Date(key.expiryDate).toLocaleDateString(),
  }));

  const renderTableBody = () => {
    return (
      <>
        <tbody>
          {cleanedData?.map(item => (
            <tr key={item.publicKey}>
              {projectsApiKeysTableColumns.map(col => (
                <TD key={col.field}>{item[col.field]}</TD>
              ))}
              <TD>
                <IconButton
                  margin="0 auto"
                  color={colors.grey[700]}
                  hoverColor={colors.error.main}
                  onClick={() => handleDeleteApiKeyClick(item.apiAccountKeyId)}
                >
                  <IconTrashX size={20} />
                </IconButton>
              </TD>
            </tr>
          ))}
        </tbody>
      </>
    );
  };

  const renderNoRows = () => {
    return (
      <>
        <tbody>
          <tr>
            <TD colSpan={4}>There are no undeleted tokens for this API account.</TD>
          </tr>
        </tbody>
      </>
    );
  };

  const renderError = () => {
    return (
      <>
        <tbody>
          <tr>
            <TD colSpan={4}>There has been an error loading the tokens for this API account.</TD>
          </tr>
        </tbody>
      </>
    );
  };

  const renderLoading = () => {
    return (
      <>
        <tbody>
          <tr>
            <TD colSpan={4}>
              <CircleLoading size={10} />
            </TD>
          </tr>
        </tbody>
      </>
    );
  };

  return (
    <>
      <Wrapper bgColor="#00000080" position="absolute" inset="0" rounded zIndex={10} />
      <Wrapper
        position="absolute"
        zIndex={10}
        top="2rem"
        width="600px"
        left="50%"
        right="50%"
        margin="0 -300px"
        bottom="0"
      >
        <Wrapper height="auto" rounded bgColor="white">
          <RowContainer padding="0.5rem 1rem" align="center" justify="space-between" width="100%">
            <Button onClick={handleOpenApiKeysDialog}>Generate New Token</Button>
            <IconButton onClick={handleCloseApiKeysTable}>
              <IconX />
            </IconButton>
          </RowContainer>
          <Divider direction="h" />
          <TableWrapper>
            <LayoutTable>
              <thead>
                <tr>
                  {projectsApiKeysTableColumns.map(col => (
                    <TH key={col.field} sortable={col.sortable}>
                      {col.name}
                    </TH>
                  ))}
                  <TH textAlign="center">Actions</TH>
                </tr>
              </thead>
              {cleanedData && cleanedData.length > 0 && renderTableBody()}
              {cleanedData && cleanedData.length == 0 && renderNoRows()}
              {isLoading && renderLoading()}
              {isError && renderError()}
            </LayoutTable>
          </TableWrapper>
        </Wrapper>
      </Wrapper>
    </>
  );
};
